import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toyotaPremiumLogo from '../../../assets/toyotaPremiun/toyota-premium-metalic-logo.png';
import useLineLiff from 'hooks/LineLiff';
import { sendToyotaPremiumMemberService } from 'pages/lexusOfficialAccount/utils/LinePostbackActions';

function ArrowIcon() {
  return (
    <svg
      className="member-services__action-icon"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none">
      <path
        d="M5.73372 12.8L10.4004 8.13338L5.73372 3.46672"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MemberServices() {
  const { liff, userInfo } = useLineLiff(
    process.env.REACT_APP_LIFF_MEMBER_SERVICE
  );

  const [memberId, setMemberId] = useState(null);
  const [carList, setCarList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  // 進入頁面時自動呼叫後端 API
  useEffect(() => {
    if (!userInfo) {
      return;
    }

    const callMemberService = async () => {
      let shouldRedirect = false;
      setInitialLoading(true);
      const idToken = liff.getIDToken();
      const accessToken = liff.getAccessToken();

      if (!liff.isLoggedIn()) {
        return;
      }

      // 如果沒有 idToken，先用 accessToken 作為 fallback（需後端配合）
      const tokenToSend = idToken || accessToken;
      if (!tokenToSend) {
        alert('無法取得 idToken 或 accessToken，請確認 LIFF 已登入與權限設定');
        return;
      }

      try {
        const result = await sendToyotaPremiumMemberService(tokenToSend);

        if (!result.success) {
          navigate('/hotai/login', { replace: true });
          return;
        }

        if (result?.success) {
          const payload = result.data || {};
          const memberIdValue = payload.member_id ?? payload.memberId ?? null;
          const carListValue = payload.car_list ?? payload.carList ?? [];

          if (Array.isArray(carListValue) && carListValue.length === 0) {
            shouldRedirect = true;
            window.location.href = 'https://toyota.tw/C8KMuQ';
            return;
          }

          setMemberId(memberIdValue);
          setCarList(carListValue);
        } else {
          console.warn(
            'sendToyotaPremiumMemberService returned non-success:',
            result
          );
        }
      } catch (err) {
        console.error('Error calling sendToyotaPremiumMemberService:', err);
        // 即使錯誤也結束初次載入狀態，讓畫面顯示錯誤或 fallback
        setInitialLoading(false);
        return;
      } finally {
        if (!shouldRedirect) {
          setInitialLoading(false);
        }
      }
    };

    callMemberService();
  }, [liff, userInfo]);

  return initialLoading ? (
    <div className="member-services-loading">載入中...</div>
  ) : (
    <div className="member-services">
      {/* Member Card */}
      <div className="member-card">
        <div className="member-card__bg-gradient-bottom"></div>
        <div className="member-card__bg-waves"></div>
        <div className="member-card__bg-stars"></div>
        <div className="member-card__content">
          <div className="member-card__top">
            <div className="member-card__info">
              <div className="member-card__heading">
                <span className="member-card__label">
                  TOYOTA PREMIUM MEMBER
                </span>
                <span className="member-card__title">尊榮車主</span>
              </div>
            </div>
            <div className="member-card__logo">
              <img src={toyotaPremiumLogo} alt="Toyota Premium logo" />
            </div>
          </div>
          <div className="member-card__id">
            <span className="member-card__id-label">會員編號</span>

            <span className="member-card__id-number">{memberId}</span>
          </div>
        </div>
      </div>
      <span className="member-services__divider mt20 mb28"></span>
      {/* 保養預約 */}
      <div className="maintenance-reservation mb28">
        <div className="member-services__section-header">
          <h2 className="member-services__section-title">保養預約</h2>
        </div>
        <div className="maintenance-reservation__vehicle-list">
          {carList.map((vehicle) => (
            <a
              className="maintenance-reservation__vehicle-item"
              key={vehicle.LICSNO}
              href={`https://liff.line.me/${
                process.env.REACT_APP_LIFF_BOOKING
              }?carNo=${encodeURIComponent(vehicle.LICSNO)}`}>
              <span className="maintenance-reservation__vehicle-item-title">
                {vehicle.LICSNO}
              </span>
              <div className="maintenance-reservation__action">
                <span className="maintenance-reservation__action-text">
                  前往預約
                </span>

                <ArrowIcon />
              </div>
            </a>
          ))}
        </div>
      </div>
      {/* 愛車資訊 */}
      <div className="my-car-info">
        <div className="member-services__section-header">
          <h2 className="member-services__section-title">TOYOTA 愛車資訊</h2>
          <p className="member-services__section-description">
            提供最完整的愛車資訊，如愛車保養資訊、回廠紀錄查詢等。
          </p>
        </div>
        <div className="my-car-info__info-list">
          <a
            className="my-car-info__info-item"
            href={`https://liff.line.me/${process.env.REACT_APP_LIFF_CAR_SECRETARY}`}
            target="_blank"
            rel="noopener noreferrer">
            <span className="my-car-info__info-item-title">
              立即前往愛車秘書
            </span>
            <ArrowIcon />
          </a>
        </div>
      </div>
    </div>
  );
}

export default MemberServices;
